import RequestMethod from './RequestMethod';
import RestRequest from './RestRequest';
import Hal from './HAL';
import RestError from './RestError';
import {Mutex} from 'async-mutex';
import {ErrorResponse} from '../../api/common';
import RefreshAuthRequest from '../../model/RefreshAuthRequest';
import {removeEmptyKeys} from '@resurs-internal/core/lib/object';
import {ensure} from '../../helpers/ensure';

export type AuthHeaderFunction = () => Promise<string>;
export type AuthRefreshFunction = (
  request?: RefreshAuthRequest,
) => Promise<void>;

export interface RestClientOptions {
  enableLog?: boolean;
  requestTimeout?: number;
  apiKey?: string;
  createAuthHeader?: AuthHeaderFunction;
  refreshAuth?: AuthRefreshFunction;
  mockFetch?: (request: RestRequest) => Promise<Response>;
}

const refreshLock = new Mutex();
const REFRESH_LOCK_TIMEOUT = 10_000;
export class RestClient {
  private readonly _enableLog: boolean;
  private readonly _requestTimeout: number;
  private readonly _apiKey: string;
  private readonly _mockFetch?: (request: RestRequest) => Promise<Response>;
  public readonly createAuthHeader: AuthHeaderFunction = async () => '';
  private readonly _refreshAuth: AuthRefreshFunction = async () => undefined;
  public readonly refreshAuth = async (request?: RefreshAuthRequest) => {
    if (process.env.NODE_ENV === 'test') {
      return await this._refreshAuth(request);
    }

    // mutex lock to avoid race condition for multiple requests
    if (refreshLock.isLocked()) return;
    await refreshLock.acquire();
    const response = await this._refreshAuth(request);
    // delay mutex release 1 min to ensure state update
    setTimeout(async () => refreshLock.release(), REFRESH_LOCK_TIMEOUT);
    return response;
  };

  constructor(options?: RestClientOptions) {
    this._enableLog = options?.enableLog ?? false;
    this._requestTimeout = options?.requestTimeout ?? 60000;
    this._apiKey = options?.apiKey ?? '';
    this.createAuthHeader = options?.createAuthHeader ?? this.createAuthHeader;
    this._refreshAuth = options?.refreshAuth ?? this._refreshAuth;
    this._mockFetch = options?.mockFetch;
  }

  readonly fetchPDF = async (
    url: string | undefined,
    headers?: Record<string, string>,
  ) => {
    return await this.fetch({
      method: 'GET',
      url,
      headers: {Accept: 'application/pdf,application/json', ...headers},
    }).then((e) => e.blob());
  };

  readonly fetchBase64PDF = async <T extends {pdfBase64: string}>(
    url: string | undefined,
    body: unknown,
    headers?: Record<string, string>,
  ) => {
    const response = await this.fetchJSONBody<T>('POST', url, body, headers);
    const base64 = response.pdfBase64;
    let buffer: Uint8Array;
    if ('atob' in window) {
      buffer = Uint8Array.from(window.atob(base64), (c) => c.charCodeAt(0));
    } else {
      buffer = Buffer.from(base64, 'base64');
    }
    Buffer.from(buffer);

    return new Blob([buffer as unknown as BlobPart], {type: 'application/pdf'});
  };

  readonly getHAL = async (object: Hal, link: string): Promise<Response> => {
    const url = object?._links?.[link]?.href;
    return await this.fetchJSON('GET', url);
  };

  readonly getJSONBody = <T>(
    url?: string,
    headers?: RestRequest['headers'],
    timeout?: number,
  ) => {
    return this.fetchJSONBody<T>('GET', url, undefined, headers, timeout);
  };

  readonly fetchJSONBody = async <T>(
    method: RequestMethod,
    url?: string,
    body?: unknown,
    headers?: RestRequest['headers'],
    timeout?: number,
  ) => {
    const response = await this.fetchJSON(method, url, body, headers, timeout);
    return await this.decodeJSON<T>(response);
  };

  readonly fetchJSON = (
    method: RequestMethod,
    url?: string,
    body?: unknown,
    headers?: RestRequest['headers'],
    timeout?: number,
  ): Promise<Response> => {
    return this.fetch({
      method,
      url,
      body: JSON.stringify(body),
      timeout,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...headers,
      },
    });
  };

  readonly decodeJSON = async <T>(response: Response): Promise<T> => {
    const responseBody = await response.json().catch(() => undefined);
    return responseBody as T;
  };

  readonly createHeaders = async () => {
    const authorization = await this.createAuthHeader();
    return removeEmptyKeys({
      Authorization: authorization,
      'Accept-Encoding': 'gzip, deflate, br',
      Connection: 'keep-alive',
      apikey: this._apiKey ?? '',
    });
  };

  readonly fetch = async (request: RestRequest): Promise<Response> => {
    const response = this._mockFetch
      ? await this._mockFetch(request)
      : await this._fetch(request);
    this._consoleLog(request, response);
    return response;
  };

  private readonly _fetch = async (request: RestRequest): Promise<Response> => {
    if (!request.url) {
      throw new Error(`Invalid URL ${request.method} ${request.url}`);
    }
    const isAuthRefresh = request.isAuthRefresh;
    const requestTimeout = request.timeout || this._requestTimeout;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), requestTimeout);

    const response: Response = await fetch(request.url, {
      method: request.method,
      mode: 'cors',
      cache: 'default',
      credentials: 'same-origin',
      redirect: 'follow',
      referrerPolicy: 'no-referrer',
      signal: controller.signal,
      body: request.body,
      headers: removeEmptyKeys({
        ...(await this.createHeaders()),
        ...request.headers,
      }),
    });

    clearTimeout(timeout);

    if (response.status === 401 && !isAuthRefresh) {
      await this.refreshAuth();
      return this.fetch({...request, isAuthRefresh: true});
    }

    return await this._validate(request, response);
  };

  private _validate = async (request: RestRequest, response: Response) => {
    if (this._isResponseOK(response)) return response;

    ensure(request.url, 'Invalid url');
    const responseBody = await response.json().catch(() => undefined);
    const error = responseBody as ErrorResponse;
    const reason = error?.message ?? response.statusText;
    const message = `${request.method} ${request.url}\n${response.status} ${reason}\nTrace ID: ${error?.traceId}`;
    throw new RestError(message, {
      method: request.method,
      url: request.url,
      status: response.status,
      code: reason,
      responseBody: responseBody,
    });
  };

  private readonly _isResponseOK = (response: Response) => {
    return response.status >= 200 && response.status <= 299;
  };

  private readonly _consoleLog = (
    request: RestRequest,
    response?: Response,
  ) => {
    if (this._enableLog) {
      console.debug(
        `${request.method} ${request.url}\n${request.body}\n${response?.status}: ${response?.statusText}`,
      );
    }
  };
}

export default RestClient;
